<script setup lang="ts">
// icons
import { faChevronLeft, faChevronRight, faMapMarked, faTrashAlt } from '@fortawesome/pro-solid-svg-icons';
import { faMapPin, faTimesCircle, faSearch } from '@fortawesome/pro-regular-svg-icons';

import { searchUiGuestAdult, searchUiGuestChild } from '@/locales/fixtures';
import { RBN_CAMPSITE_DETAIL } from '@/utils/constants';

// components
import CiLocationInput from '@/components/CiSearch/Location/CiLocationInput.vue';
// import CiSkeletonDatepicker from '@/components/skeleton/CiSkeletonDatepicker.vue';

// /********************
//  * PROPS & EMITS     *
//  ********************/
const emit = defineEmits<{
  close: [];
}>();

/********************
 * COMPOSITIONS      *
 ********************/
const getRouteBaseName = useRouteBaseName();
const loadingStore = useLoadingStore();
const route = useRoute();
const searchStore = useSearchStore();
const { $gettext, $pgettext } = useGettext();
const { locale } = useI18n();

const locationInputRef = ref<InstanceType<typeof CiLocationInput> | null>(null);
const contentTransition = ref('slide-next');
const {
  ages,
  childAgeMissing,
  closeOpenType,
  datePickerValues,
  getChildAge,
  hasCampsite,
  hasLocation,
  i18nPromoCampsitesTitle,
  i18nSuggestionTitle,
  localChildrenCount,
  onAccommodationChange,
  onChildAgeChange,
  onDateChange,
  onDateClear,
  onFlexChange,
  onLocationChange,
  onLocationClear,
  onLocationInputFocus,
  onLocationSave,
  onLocationSelected,
  onPersonCountChange,
  onSave,
  onSearchSubmit,
  openOpenType,
  pointer,
  showSuggestions,
} = useSearch({
  contentTransition,
});

const {
  i18nFlexDayOne,
  i18nFlexDayThree,
  i18nFlexDayTwo,
  i18nFlexHeadline,
  i18nLabelPitch,
  i18nLabelRentals,
  i18nResetSelection,
} = useI18nSearchAvailabilities();

/********************
 * REFS & VARS       *
 ********************/
// const CiDatepicker = defineAsyncComponent({
//   loader: () => import('@/components/CiDatepicker/CiDatepicker.vue'),
//   loadingComponent: CiSkeletonDatepicker,
// });

// computed: {
//   datePickerConfig() {
//     return {
//       align: 'left',
//       birthdateMode: false,
//       breakpoint: 800,
//       closeAfterSelect: false,
//       dateFormat: 'yyyy-MM-dd',
//       dateFormatUseRange: true,
//       displayMonths: 2,
//       inline: true,
//       maxDate: null,
//       minDate: new Date(),
//       minDays: 2,
//       openTopOffset: 2,
//       range: true,
//       selectStyle: 'day',
//       showTooltip: true,
//       i18n: {
//         inputFormat: 'MM-dd-yyyy',
//         nights: [this.$gettext('night'), this.$gettext('nights')],
//         until: this.$gettext('until'),
//         today: this.$gettext('today'),
//         placeholder: '',
//         inputPlaceholder: '',
//       },
//       pickerWidthPixelOffset: -1,
//     };
//   },
// },

/********************
 * WATCHER           *
 ********************/
// set focus on location input
watch(
  () => locationInputRef.value,
  (newValue) => {
    if (newValue) {
      nextTick(() => {
        newValue.setFocus();
      });
    }
  },
);

/********************
 * HOOKS             *
 ********************/
onMounted(() => {
  if (searchStore.searchOpenType === 'location') {
    searchStore.getSuggestions({ lang: locale.value, query: searchStore.searchParams.q });
  }
});

/********************
 * FUNCTIONS         *
 ********************/
function onClose() {
  closeOpenType();
  emit('close');
}

function onPrevious(openType) {
  contentTransition.value = 'slide-prev';

  switch (openType) {
    case 'date':
      openOpenType('location');
      break;
    case 'accommodation':
      openOpenType('date');
      break;
    case 'traveller':
      openOpenType('accommodation');
      break;
  }
}

function onNext(openType) {
  onSave(openType);
}

function onSkip(openType) {
  contentTransition.value = 'slide-next';

  switch (openType) {
    case 'location':
      openOpenType('date');
      break;
    case 'date':
      openOpenType('accommodation');
      break;
    case 'accommodation':
      openOpenType('traveller');
      break;
  }
}

function onSubmit(openType) {
  onSave(openType);

  if (onSearchSubmit()) {
    emit('close');
  }
}
</script>

<template>
  <form
    class="search-modal flex h-full flex-col bg-white"
    novalidate
    @keypress.enter.prevent="onSave(searchStore.searchOpenType)"
  >
    <div class="search-modal__header py-4">
      <div class="flex items-center justify-between">
        <CiButton
          tag="button"
          variant="text"
          size="icon"
          :class="{
            invisible:
              searchStore.searchOpenType === 'location'
              || (searchStore.searchOpenType === 'date' && getRouteBaseName(route) === RBN_CAMPSITE_DETAIL),
          }"
          @click="onPrevious(searchStore.searchOpenType)"
        >
          <span class="sr-only">{{
            $pgettext('Search modal action step back - will not be displayed', 'Step Back')
          }}</span>
          <CiAwesomeIcon
            :icon="faChevronLeft"
            ratio="0.6"
          />
        </CiButton>

        <transition
          :name="contentTransition"
          mode="out-in"
        >
          <!-- location -->
          <div
            v-if="searchStore.searchOpenType === 'location'"
            key="location"
          >
            <div class="m-0 font-medium">
              {{ $pgettext('Search flyout headline location', 'Where do you want to camp?') }}
            </div>
          </div>

          <!-- date -->
          <div
            v-else-if="searchStore.searchOpenType === 'date'"
            key="date"
          >
            <div class="m-0 font-medium">
              {{ $pgettext('Search flyout headline date', 'When do you want to camp?') }}
            </div>
          </div>

          <!-- accommodation -->
          <div
            v-else-if="searchStore.searchOpenType === 'accommodation'"
            key="accommodation"
          >
            <div class="m-0 font-medium">
              {{ $pgettext('Search flyout headline accommodation', 'How would you like to camp?') }}
            </div>
          </div>

          <!-- traveller -->
          <div
            v-else-if="searchStore.searchOpenType === 'traveller'"
            key="traveller"
          >
            <div class="m-0 font-medium">
              {{ $pgettext('Search flyout headline traveller', 'How many people would like to camp?') }}
            </div>
          </div>
        </transition>

        <CiButton
          tag="button"
          variant="text"
          size="icon"
          @click.prevent="onClose"
        >
          <span class="sr-only">{{ $gettext('Close') }}</span>
          <CiAwesomeIcon
            :icon="faTimesCircle"
            ratio="0.8"
          />
        </CiButton>
      </div>
    </div>

    <div class="flex-1 overflow-auto overflow-x-hidden">
      <transition
        :name="contentTransition"
        mode="out-in"
      >
        <!-- location -->
        <div
          v-if="searchStore.searchOpenType === 'location'"
          key="location"
          class="my-4"
        >
          <div class="mx-4 pb-2">
            <CiLocationInput
              ref="locationInputRef"
              :search-string="searchStore.searchParams.q"
              :is-submit="searchStore.searchTouched && route.name !== 'homeLanguage'"
              @focus="onLocationInputFocus"
              @change="onLocationChange"
              @clear="onLocationClear"
              @submit="onLocationSave(emit)"
            />
          </div>

          <CiSearchResultList
            v-if="showSuggestions && hasLocation"
            :active="pointer"
            :results="searchStore.suggestions.locations"
            :title="''"
            :icon="faMapMarked"
            title-prop="name"
            subtitle-prop="subtitle"
            @search-result-list-click="onLocationSelected($event, emit)"
          />

          <CiSearchResultList
            v-if="
              showSuggestions
                && hasCampsite
                && !(loadingStore.isLoading('getSuggestions') && !searchStore.suggestions.locations.length)
            "
            :active="pointer"
            :index-offset="searchStore.suggestions.locations.length"
            :results="searchStore.suggestions.campsites"
            :title="searchStore.searchParams.q.length > 2 ? i18nSuggestionTitle : i18nPromoCampsitesTitle"
            :icon="faMapPin"
            :no-results-message="true"
            title-prop="name"
            subtitle-prop="campsite"
            image-prop="icon_8_5"
            @search-result-list-click="onLocationSelected($event, emit)"
          />
        </div>

        <!-- date -->
        <div
          v-else-if="searchStore.searchOpenType === 'date'"
          key="date"
          class="m-4"
        >
          <LazyCiDatepicker
            :value="datePickerValues"
            @change="onDateChange"
          />
          <div class="flex flex-col gap-4 divide-y divide-gray-30 py-4">
            <transition
              name="translate-y-up"
              mode="out-in"
            >
              <div
                v-if="datePickerValues[0] && datePickerValues[1]"
                class="flex flex-col gap-4"
              >
                <div>
                  <div class="m-0 font-medium">
                    {{ i18nFlexHeadline }}
                  </div>
                  <div class="mt-2 flex flex-wrap justify-between space-y-2 min-[350px]:flex-nowrap min-[350px]:space-x-2 min-[350px]:space-y-0">
                    <CiFilterBoolean
                      class="w-full whitespace-nowrap"
                      :value="searchStore.searchParams.flex === 1"
                      :name="'1'"
                      :label="i18nFlexDayOne"
                      @input="onFlexChange($event, 1)"
                    />
                    <CiFilterBoolean
                      class="w-full whitespace-nowrap"
                      :value="searchStore.searchParams.flex === 2"
                      :name="'2'"
                      :label="i18nFlexDayTwo"
                      @input="onFlexChange($event, 2)"
                    />
                    <CiFilterBoolean
                      class="w-full whitespace-nowrap"
                      :value="searchStore.searchParams.flex === 3"
                      :name="'3'"
                      :label="i18nFlexDayThree"
                      @input="onFlexChange($event, 3)"
                    />
                  </div>
                </div>
              </div>
            </transition>

            <div v-if="datePickerValues[0] || datePickerValues[1]">
              <CiButton
                tag="button"
                variant="danger"
                class="mt-4"
                @click="onDateClear"
              >
                <CiAwesomeIcon
                  class="mr-2"
                  :icon="faTrashAlt"
                  ratio="0.6"
                />
                {{ i18nResetSelection }}
              </CiButton>
            </div>
          </div>
        </div>

        <div
          v-else-if="searchStore.searchOpenType === 'accommodation'"
          key="accommodation"
          class="m-3"
        >
          <div class="flex flex-col">
            <CiFilterBoolean
              key="pitch"
              class="my-2"
              :value="searchStore.searchParams.acco_type === 'pitch' || searchStore.searchParams.acco_type === 'all'"
              :name="i18nLabelPitch"
              :label="i18nLabelPitch"
              @input="onAccommodationChange($event, 'pitch')"
            >
              <img
                src="~/assets/svg/icon-pitch.svg"
                class="mr-4"
                :alt="`icon ${i18nLabelPitch}`"
              />
            </CiFilterBoolean>
            <CiFilterBoolean
              key="rentals"
              class="my-2"
              :value="searchStore.searchParams.acco_type === 'acco' || searchStore.searchParams.acco_type === 'all'"
              :name="i18nLabelRentals"
              :label="i18nLabelRentals"
              @input="onAccommodationChange($event, 'acco')"
            >
              <img
                src="~/assets/svg/icon-cabin.svg"
                class="mr-3"
                :alt="`icon ${i18nLabelRentals}`"
              />
            </CiFilterBoolean>
          </div>
        </div>

        <div
          v-else-if="searchStore.searchOpenType === 'traveller'"
          key="traveller"
          class="m-3"
        >
          <!-- guests -->
          <CiGuest
            class="mb-4"
            :amount="searchStore.searchParams.adults"
            :guest="searchUiGuestAdult"
            :max="20"
            :min="1"
            @change="onPersonCountChange"
          >
            <img
              src="~/assets/svg/icon-parents.svg"
              width="30"
              height="30"
              alt="Adults"
            />
          </CiGuest>
          <CiGuest
            :amount="localChildrenCount"
            :guest="searchUiGuestChild"
            :max="20"
            :min="0"
            @change="onPersonCountChange"
          >
            <img
              src="~/assets/svg/icon-children.svg"
              width="25"
              height="25"
              alt="Children"
            />
          </CiGuest>

          <!-- age -->
          <div
            v-if="localChildrenCount > 0"
            class="mt-12"
          >
            <div class="m-0 font-medium">
              {{ $gettext('Age of the children on the day of arrival?') }}
            </div>

            <template
              v-for="(_, idx) in localChildrenCount"
              :key="idx"
            >
              <div v-if="!getChildAge(idx)?.key && childAgeMissing" class="-mb-2 mt-2 text-sm text-danger">
                {{ $gettext('Please specify age') }}
              </div>
              <CiSelect
                :name="`childAge-${idx}`"
                :options="ages"
                class="mt-4 block w-full"
                :value="getChildAge(idx)"
                show-disabled-option
                @change="onChildAgeChange($event, idx)"
              />
            </template>
          </div>
        </div>
      </transition>
    </div>

    <div class="border-t border-gray-20">
      <div class="container-fluid">
        <div class="my-4 flex justify-between">
          <CiButton
            v-if="searchStore.searchOpenType !== 'traveller' && !searchStore.searchTouched"
            tag="button"
            variant="text"
            @click="onSkip(searchStore.searchOpenType)"
          >
            {{ $pgettext('Search flyout action skip', 'Skip') }}
          </CiButton>

          <CiButton
            v-if="
              searchStore.searchOpenType !== 'traveller'
                && (!searchStore.searchTouched || route.name === 'homeLanguage')
            "
            tag="button"
            class="ml-auto"
            @click.prevent="onNext(searchStore.searchOpenType)"
          >
            <span>{{ $pgettext('Search flyout action next', 'Next') }}</span>
            <CiAwesomeIcon
              class="ml-2"
              :icon="faChevronRight"
              ratio="0.6"
            />
          </CiButton>

          <CiButton
            v-else
            tag="button"
            variant="warning"
            class="w-full"
            @click.prevent="onSubmit(searchStore.searchOpenType)"
          >
            <CiAwesomeIcon
              class="mr-2"
              :icon="faSearch"
              ratio="0.6"
            />
            <span
              key="search"
            >{{
              $pgettext('Search button text', 'Search')
            }}</span>
          </CiButton>
        </div>
      </div>
    </div>
  </form>
</template>
